import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getHostBookingsOnly } from '../../../services/firestore/bookings';
import { useAuth } from '../../../contexts/AuthContext';
import { Package, Calendar, MapPin, Users, Mail, Phone } from 'lucide-react';

const HostBookings = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      try {
        const bookingsData = await getHostBookingsOnly(user.uid).catch(() => []);
        setBookings(bookingsData);
      } catch (error) {
        console.error('Error loading bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Bookings</h1>

        {bookings.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow-md">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">No bookings yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => {
              const selectedDate = booking.selectedDate 
                ? (booking.selectedDate?.toDate ? booking.selectedDate.toDate() : new Date(booking.selectedDate))
                : null;

              return (
                <Link
                  key={booking.id}
                  to={`/host/bookings/${booking.id}`}
                  className="block bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {booking.tripTitle || `Booking #${booking.id?.substring(0, 8)}`}
                        </h3>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-medium ${
                            booking.status === 'confirmed'
                              ? 'bg-green-100 text-green-800'
                              : booking.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : booking.status === 'canceled'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {booking.status}
                        </span>
                      </div>
                      
                      {booking.tripLocation && (
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <MapPin className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm">{booking.tripLocation}</span>
                        </div>
                      )}
                      
                      {selectedDate && (
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Calendar className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm">
                            Arrival: {selectedDate.toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}
                          </span>
                        </div>
                      )}
                      
                      {booking.guestCount && (
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Users className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm">
                            {booking.guestCount} {booking.guestCount === 1 ? 'guest' : 'guests'}
                          </span>
                        </div>
                      )}
                      
                      {booking.firstName && booking.lastName && (
                        <div className="mt-2 text-sm text-gray-600">
                          <span className="font-medium">Guest:</span> {booking.firstName} {booking.lastName}
                        </div>
                      )}
                      
                      {booking.email && (
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Mail className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm">{booking.email}</span>
                        </div>
                      )}
                      
                      {booking.phone && (
                        <div className="flex items-center gap-2 text-gray-600 mb-1">
                          <Phone className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm">{booking.phone}</span>
                        </div>
                      )}
                      
                      {booking.totalAmount && (
                        <p className="text-lg font-bold text-emerald-700 mt-2">
                          Total: ${typeof booking.totalAmount === 'number' ? booking.totalAmount.toFixed(2) : booking.totalAmount}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default HostBookings;

